import { Component, NgZone, OnInit } from '@angular/core';
import {
  AnalyticsEventData,
  AnalyticsService,
  CustomerService,
  TranslationService,
} from '@proximus/ng-common';
import { OnDestroyHookDirective } from '../../shared/hooks';
import { CloudService } from '../services/cloud.service';
import { Store } from '@ngrx/store';
import {
  ApplicationState,
  getCloudAccountActivationError,
  getCloudAccountActivationPending,
  getCloudAccountActivationSuccess,
  getCloudAccountActive,
  getCloudAccountLogin,
  getCloudAccountStatusError,
  getCloudAccountStatusFetchedSuccess,
  getCloudAccountStatusFetching,
} from '../../store/reducers';
import { Alert, AlertsService } from '@proximus/ng-common-components';
import {
  ActivateCloudAccount,
  GetCloudActivationStatus,
} from '../store/actions/cloud.actions';
import { getPiiCloudServiceId } from '../../shared/pii/pii.utils';

@Component({
  selector: 'mprd-cloud-access',
  templateUrl: './cloud-access.component.html',
  styleUrls: ['./cloud-access.component.css'],
})
export class CloudAccessComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  serviceId: string;

  login: string;
  loginFallback: string;

  accountActive: boolean;
  accountStatusFetching: boolean;
  accountStatusFetchedSuccess: boolean;
  accountStatusError: boolean;

  accountActivationPending: boolean;
  accountActivationSuccess: boolean;
  accountActivationError: boolean;

  tncAccepted: boolean;

  constructor(
    private store: Store<ApplicationState>,
    private customerService: CustomerService,
    private cloudService: CloudService,
    private translationService: TranslationService,
    private alertService: AlertsService,
    private ngZone: NgZone,
    private analyticsService: AnalyticsService,
  ) {
    super();
  }

  ngOnInit() {
    this.serviceId = getPiiCloudServiceId();

    this.addSubscription(
      this.customerService.getCustomerAccount().subscribe((value) => {
        this.loginFallback = value.login;
      }),
    );

    this.addSubscription(
      this.store.select(getCloudAccountActive).subscribe((active) => {
        this.accountActive = active;
      }),
    );
    this.addSubscription(
      this.store.select(getCloudAccountLogin).subscribe((login) => {
        this.login = login;
      }),
    );
    this.addSubscription(
      this.store
        .select(getCloudAccountStatusFetchedSuccess)
        .subscribe((value) => {
          this.accountStatusFetchedSuccess = value;
        }),
    );
    this.addSubscription(
      this.store.select(getCloudAccountStatusFetching).subscribe((fetching) => {
        this.accountStatusFetching = fetching;
      }),
    );
    this.addSubscription(
      this.store.select(getCloudAccountStatusError).subscribe((error) => {
        this.accountStatusError = !!error;
        if (error) {
          const alert: Alert = {
            isClosable: false,
            alertKey: 'cloudStatusAlert',
            title: this.translationService.getTranslatedValueFor(
              'cloud.access.status.error.title',
            ),
            description: this.translationService.getTranslatedValueFor(
              'cloud.access.status.error.description',
            ),
            type: 'error',
          };
          this.alertService.dispatchAlert(alert);
        }
      }),
    );
    this.addSubscription(
      this.store.select(getCloudAccountActivationPending).subscribe((value) => {
        this.accountActivationPending = value;
      }),
    );
    this.addSubscription(
      this.store.select(getCloudAccountActivationSuccess).subscribe((value) => {
        this.accountActivationSuccess = value;
        if (value) {
          this.store.dispatch(
            new GetCloudActivationStatus({ serviceId: this.serviceId }),
          );
        }
      }),
    );
    this.addSubscription(
      this.store.select(getCloudAccountActivationError).subscribe((error) => {
        this.accountActivationError = !!error;
        if (error) {
          const alert: Alert = {
            isClosable: false,
            alertKey: 'cloudActivationAlert',
            title: this.translationService.getTranslatedValueFor(
              'cloud.access.activation.error.title',
            ),
            description: this.translationService.getTranslatedValueFor(
              'cloud.access.activation.error.description',
            ),
            type: 'error',
          };
          this.alertService.dispatchAlert(alert);
          const event: AnalyticsEventData = {
            event: 'click',
            event_location: 'myproducts-cloud',
            event_detail: 'create a cloud account',
            error: 'error',
            errorDetail: 'Failed to fetch cloud account status',
          };
          this.analyticsService.reportEvent(event);
        }
      }),
    );

    this.store.dispatch(
      new GetCloudActivationStatus({ serviceId: this.serviceId }),
    );
  }

  toggleTnc() {
    this.ngZone.run(() => (this.tncAccepted = !this.tncAccepted));
  }

  activate() {
    if (this.tncAccepted) {
      this.store.dispatch(
        new ActivateCloudAccount({ serviceId: this.serviceId }),
      );
    }
  }
}
